/* =========================================================================
   Storage layer — IndexedDB
   -------------------------------------------------------------------------
   Iteration 1 kept everything in localStorage as one JSON string, with photos
   inlined as base64 data URLs. localStorage caps at ~5MB per origin and base64
   adds ~33% overhead, so the collection hit a hard wall at roughly 15-25 photos
   and saving started failing.

   Here, recipes live in IndexedDB and photos are stored as real Blobs, which
   means no base64 inflation and a quota measured in hundreds of MB or more.
   ========================================================================= */

const DB_NAME = 'killerRecipes'
const DB_VERSION = 1
const STORE = 'recipes'
const META = 'meta'

let _db = null

function openDB() {
  if (_db) return Promise.resolve(_db)
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains(META)) {
        db.createObjectStore(META, { keyPath: 'k' })
      }
    }
    req.onsuccess = () => {
      _db = req.result
      // If another tab upgrades the schema, close here so it isn't blocked.
      _db.onversionchange = () => { _db.close(); _db = null }
      resolve(_db)
    }
    req.onerror = () => reject(req.error)
    req.onblocked = () => reject(new Error('Database blocked by another open tab'))
  })
}

function tx(store, mode, fn) {
  return openDB().then(db => new Promise((resolve, reject) => {
    const t = db.transaction(store, mode)
    const s = t.objectStore(store)
    let out
    try { out = fn(s) } catch (e) { reject(e); return }
    t.oncomplete = () => resolve(out && out.result !== undefined ? out.result : out)
    t.onerror = () => reject(t.error)
    t.onabort = () => reject(t.error || new Error('Transaction aborted'))
  }))
}

/* ---------------- recipe CRUD ---------------- */

export async function allRecipes() {
  const rows = await tx(STORE, 'readonly', s => s.getAll())
  return (rows || []).sort((a, b) => (a.pos ?? 0) - (b.pos ?? 0))
}

export function putRecipe(recipe) {
  return tx(STORE, 'readwrite', s => s.put(recipe))
}

export function putManyRecipes(list) {
  return tx(STORE, 'readwrite', s => { list.forEach(r => s.put(r)) })
}

export function deleteRecipe(id) {
  return tx(STORE, 'readwrite', s => s.delete(id))
}

export function clearRecipes() {
  return tx(STORE, 'readwrite', s => s.clear())
}

/* ---------------- small key/value meta store ---------------- */

export async function getMeta(k) {
  const row = await tx(META, 'readonly', s => s.get(k))
  return row ? row.v : undefined
}

export function setMeta(k, v) {
  return tx(META, 'readwrite', s => s.put({ k, v }))
}

/* ---------------- storage estimate & persistence ---------------- */

/**
 * Ask the browser to make our storage persistent.
 *
 * This matters a lot on iOS: Safari evicts IndexedDB for sites you haven't
 * visited in ~7 days. Installing the app to the home screen makes eviction
 * far less likely, and this request makes it less likely again.
 */
export async function requestPersistence() {
  try {
    if (!navigator.storage?.persist) return { supported: false, persisted: false }
    const already = await navigator.storage.persisted?.()
    if (already) return { supported: true, persisted: true }
    const granted = await navigator.storage.persist()
    return { supported: true, persisted: granted }
  } catch {
    return { supported: false, persisted: false }
  }
}

export async function storageEstimate() {
  try {
    if (!navigator.storage?.estimate) return null
    const { usage, quota } = await navigator.storage.estimate()
    return { usage: usage || 0, quota: quota || 0 }
  } catch {
    return null
  }
}
